import type { PostCidCvnCaseStatusBody } from '@packages/clients';

import { getLogger } from '@packages/core/logging';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { buildCidCvnSubmissionBody } from '../../cases/helpers';

const logger = getLogger('techline.cid-cvn');

/** Parameters for submitting a CID-CVN case for review. */
export type SubmitCidCvnCaseParams = {
    /** The case number to submit. */
    caseNumber: string;

    /** The retailer number associated with the case. */
    retailerNumber: string | undefined;

    /** The worksheet submission ID. */
    submissionId: string;

    /** The username making the submission. */
    username: string;

    /** The worksheet ID. */
    worksheetId: string;
};

/**
 * Submit a CID-CVN case for review by transitioning its status to
 * IN_REVIEW with the worksheet submission entry. Throws an error with
 * a user-facing message if the submission fails.
 */
export class SubmitCidCvnCase extends StsBackendOperation {
    /**
     * Build the submission payload and update the case status to IN_REVIEW.
     *
     * @param params - the submission details and identity fields
     * @throws {Error} if the submission fails, with a user-facing message
     */
    async exec(params: SubmitCidCvnCaseParams): Promise<void> {
        try {
            // Build the submission payload with IN_REVIEW status transition
            const updateBody = buildCidCvnSubmissionBody(params) as PostCidCvnCaseStatusBody;

            // Transition the case status via the CID-CVN status endpoint
            const response = await this.client.updateCidCvnCaseStatus({ updateBody });

            if (!response.success) {
                const errorMessage =
                    response.data?.message || 'We encountered an error while submitting your case. Please try again.';
                throw new Error(errorMessage);
            }
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to submit case';
            logger.error('Failed to submit the CID/CVN case', { error: e });
            throw e instanceof Error ? e : new Error(message);
        }
    }
}
